/**
 * ConnectProvider Component
 *
 * Shown after registration or login if no email provider is connected.
 * Allows the user to connect Gmail or Microsoft 365, or skip for now.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { API_BASE } from '../../lib/api-config';

interface ConnectProviderProps {
  onSkip: () => void;
}

type Provider = 'gmail' | 'microsoft';

export function ConnectProvider({ onSkip }: ConnectProviderProps) {
  const navigate = useNavigate();
  const { token, fetchConnections } = useAuthStore();
  const [connecting, setConnecting] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const microsoftEnabled = false;

  const handleConnect = useCallback(
    async (provider: Provider) => {
      setConnecting(provider);
      setError(null);

      try {
        const res = await fetch(`${API_BASE}/oauth/${provider}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let errorMessage = 'Failed to connect';
        if (!res.ok) {
          try {
            const data = await res.json() as { message?: string; error?: string };
            errorMessage = data.message ?? data.error ?? `Server error ${res.status}`;
          } catch {
            errorMessage = `Server error ${res.status}`;
          }
          throw new Error(errorMessage);
        }
        const data = await res.json() as { url: string };
        window.location.href = data.url;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to connect');
      } finally {
        setConnecting(null);
      }
    },
    [token]
  );

  const handleSkip = useCallback(() => {
    // Refresh connections after skip (in case they were set via OAuth callback)
    fetchConnections();
    onSkip();
  }, [fetchConnections, onSkip]);

  return (
    <div
      className="flex flex-col items-center justify-center min-h-full px-4"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <div
        className="w-full max-w-md p-8 rounded-lg"
        style={{
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border)',
        }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="text-2xl font-semibold tracking-tight mb-2"
            style={{
              color: 'var(--text-primary)',
              fontFamily: 'DM Sans, system-ui, sans-serif',
            }}
          >
            Connect your email
          </div>
          <p
            className="text-sm leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}
          >
            Link your email account to start using Signa.
            Your emails are encrypted. We never see your data.
          </p>
        </div>

        {/* Connection buttons */}
        <div className="space-y-3">
          {/* Gmail */}
          <button
            type="button"
            onClick={() => handleConnect('gmail')}
            disabled={connecting !== null}
            className="w-full flex items-center gap-4 p-4 rounded-lg transition-all disabled:opacity-50"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
            }}
            onMouseEnter={(e) => {
              if (!connecting) {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.backgroundColor = 'var(--bg-elevated)';
            }}
          >
            {/* Gmail mail icon */}
            <div
              className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect
                  x="3.5"
                  y="6"
                  width="17"
                  height="12"
                  rx="2.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="M4.5 7.5l7.5 6 7.5-6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.5 16.5l4.8-4.1M19.5 16.5l-4.8-4.1"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.72"
                />
              </svg>
            </div>

            <div className="flex-1 text-left">
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--text-primary)' }}
              >
                Connect Gmail
              </div>
              <div
                className="text-xs mt-0.5"
                style={{ color: 'var(--text-tertiary)' }}
              >
                Read, send &amp; organize emails
              </div>
            </div>

            {connecting === 'gmail' ? (
              <div className="flex-shrink-0">
                <div
                  className="w-5 h-5 rounded-full border-2"
                  style={{
                    borderColor: 'var(--accent)',
                    borderTopColor: 'transparent',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              </div>
            ) : (
              <svg
                className="flex-shrink-0"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M6 3l5 5-5 5"
                  stroke="var(--text-tertiary)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>

          {/* Microsoft 365 */}
          <button
            type="button"
            onClick={() => microsoftEnabled && handleConnect('microsoft')}
            disabled={connecting !== null || !microsoftEnabled}
            className="w-full flex items-center gap-4 p-4 rounded-lg transition-all disabled:opacity-50"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              cursor: microsoftEnabled ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => {
              if (!connecting && microsoftEnabled) {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.backgroundColor = 'var(--bg-elevated)';
            }}
          >
            <div
              className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
              style={{
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border)',
              }}
            >
              {/* Microsoft grid icon */}
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                aria-hidden="true"
                style={{ color: 'var(--text-secondary)' }}
              >
                <rect x="2" y="2" width="10" height="10" fill="currentColor" rx="1.5" />
                <rect x="16" y="2" width="10" height="10" fill="currentColor" rx="1.5" opacity="0.78" />
                <rect x="2" y="16" width="10" height="10" fill="currentColor" rx="1.5" opacity="0.78" />
                <rect x="16" y="16" width="10" height="10" fill="currentColor" rx="1.5" />
              </svg>
            </div>

            <div className="flex-1 text-left">
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--text-primary)' }}
              >
                Connect Microsoft 365
              </div>
              <div
                className="text-xs mt-0.5"
                style={{ color: 'var(--text-tertiary)' }}
              >
                Coming soon
              </div>
            </div>

            {connecting === 'microsoft' ? (
              <div className="flex-shrink-0">
                <div
                  className="w-5 h-5 rounded-full border-2"
                  style={{
                    borderColor: 'var(--accent)',
                    borderTopColor: 'transparent',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              </div>
            ) : (
              <svg
                className="flex-shrink-0"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M6 3l5 5-5 5"
                  stroke="var(--text-tertiary)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div
            className="mt-4 p-3 rounded-lg text-xs leading-relaxed"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--error) 10%, transparent)',
              border: '1px solid var(--error)',
              color: 'var(--error)',
            }}
          >
            {error}
          </div>
        )}

        {/* Skip link */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={handleSkip}
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
          >
            Skip for now - go to app
          </button>
        </div>
      </div>

      {/* Security note */}
      <div
        className="mt-6 flex items-center gap-2 text-xs"
        style={{ color: 'var(--text-tertiary)' }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M6 1L1.5 3v3c0 2.485 1.95 4.81 4.5 5.5C8.55 10.81 10.5 8.485 10.5 6V3L6 1z"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
          />
        </svg>
        Your emails are encrypted with your master password. We never see your data.
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
